<script setup lang="ts">
import { computed, ref } from 'vue'
import { CollapsibleContent, CollapsibleRoot, CollapsibleTrigger } from 'reka-ui'
import DynamicHeading from '@/components/DynamicHeading.vue'

const props = withDefaults(defineProps<{
  isInitiallyExpanded?: boolean
  isIndented?: boolean
  /** Wraps the trigger in a heading of this level, so the section shows up in heading navigation. */
  headingLevel?: number
}>(), {
  isInitiallyExpanded: false,
  isIndented: false
})

const emit = defineEmits<{
  toggle: [isExpanded: boolean]
}>()

defineSlots<{
  label?(): unknown
  default(): unknown
}>()

const isExpanded = ref(props.isInitiallyExpanded)

const triggerIndentClass = computed(() => (props.isIndented ? 'pl-16' : 'pl-4'))
const contentTopPaddingClass = computed(() => (props.isIndented ? 'pt-4' : 'pt-5'))

function setExpanded(expanded: boolean) {
  isExpanded.value = expanded
  emit('toggle', expanded)
}
</script>

<template>
  <CollapsibleRoot :open="isExpanded" @update:open="setExpanded">
    <component :is="headingLevel ? DynamicHeading : 'div'" :heading-level="headingLevel">
      <CollapsibleTrigger
        class="relative block h-10 w-full bg-gray-100 pr-4 text-left text-lg font-bold text-gray-700 hover:underline hover:underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400"
        :class="triggerIndentClass"
        data-testid="collapsible-goal-section-trigger"
      >
        <slot name="label">Examples</slot>
        <span
          class="material-icons-round absolute right-4 top-1/2 -translate-y-1/2 text-display-sm transition-transform"
          :class="{ 'rotate-180': isExpanded }"
          aria-hidden="true"
        >expand_more</span>
      </CollapsibleTrigger>
    </component>
    <CollapsibleContent
      class="px-4 pb-4"
      :class="contentTopPaddingClass"
      data-testid="collapsible-goal-section-content"
    >
      <slot />
    </CollapsibleContent>
  </CollapsibleRoot>
</template>
